import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export type LayerId = 'dns-infrastructure' | 'darkapi';

export interface LayerConfig {
  id: LayerId;
  label: string;
  visible: boolean;
  opacity: number;          // 0-1
  filterMinScore: number;   // 0-100 threshold
  loading: boolean;
  error: string | null;
}

interface LayerState {
  layers: Record<LayerId, LayerConfig>;
}

interface LayerActions {
  toggleLayer: (id: LayerId) => void;
  setOpacity: (id: LayerId, opacity: number) => void;
  setFilterMinScore: (id: LayerId, score: number) => void;
  setLoading: (id: LayerId, loading: boolean) => void;
  setError: (id: LayerId, error: string | null) => void;
}

const defaultLayers: Record<LayerId, LayerConfig> = {
  'dns-infrastructure': {
    id: 'dns-infrastructure',
    label: 'DNS Infrastructure',
    visible: true,
    opacity: 1.0,
    filterMinScore: 0,
    loading: false,
    error: null,
  },
  darkapi: {
    id: 'darkapi',
    label: 'DarkAPI Threats',
    visible: false,
    opacity: 0.85,
    filterMinScore: 0,
    loading: false,
    error: null,
  },
};

export const useLayerStore = create<LayerState & LayerActions>()(
  immer((set) => ({
    layers: defaultLayers,

    toggleLayer: (id) =>
      set((state) => {
        state.layers[id].visible = !state.layers[id].visible;
      }),

    setOpacity: (id, opacity) =>
      set((state) => {
        state.layers[id].opacity = Math.max(0, Math.min(1, opacity));
      }),

    setFilterMinScore: (id, score) =>
      set((state) => {
        state.layers[id].filterMinScore = Math.max(0, Math.min(100, score));
      }),

    setLoading: (id, loading) =>
      set((state) => {
        state.layers[id].loading = loading;
      }),

    setError: (id, error) =>
      set((state) => {
        state.layers[id].error = error;
      }),
  })),
);
